import { useCallback, useEffect, useMemo, useState } from 'react';
import { clearSession, getStoredSession, saveSession } from '../services/auth';
import * as api from '../services/api';
import type { Username } from '../utils/constants';

type AuthState = {
  status: 'checking' | 'authenticated' | 'anonymous';
  username: Username | null;
  token: string | null;
  error: string;
};

export function useAuth() {
  const [state, setState] = useState<AuthState>(() => {
    const stored = getStoredSession();
    return stored
      ? { status: 'checking', username: stored.username, token: stored.token, error: '' }
      : { status: 'checking', username: null, token: null, error: '' };
  });

  useEffect(() => {
    const stored = getStoredSession();

    api
      .me(stored?.token)
      .then((result) => {
        const session = { username: result.username, token: result.csrfToken };
        saveSession(session);
        setState({ status: 'authenticated', username: session.username, token: session.token, error: '' });
      })
      .catch((error: unknown) => {
        clearSession();
        setState({
          status: 'anonymous',
          username: null,
          token: null,
          error: error instanceof Error ? error.message : 'Your session expired. Please log in again.',
        });
      });
  }, []);

  const login = useCallback(async (username: Username, password: string) => {
    const session = await api.login(username, password);
    saveSession(session);
    setState({ status: 'authenticated', username: session.username, token: session.token, error: '' });
  }, []);

  const logout = useCallback(async () => {
    const token = state.token;
    clearSession();
    setState({ status: 'anonymous', username: null, token: null, error: '' });

    if (token) {
      await api.logout(token).catch(() => undefined);
    }
  }, [state.token]);

  return useMemo(
    () => ({
      ...state,
      login,
      logout,
    }),
    [login, logout, state],
  );
}
